import { FilterList as FilterListIcon } from '@mui/icons-material';
import {
  Box,
  ClickAwayListener,
  FormControlLabel,
  Grow,
  Paper,
  Popper,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import React, { useRef, useState } from 'react';

import {
  getDefaultSortField,
  getDefaultSortOrder,
  isValidSortField,
  isValidSortOrder,
  SORT_OPTIONS,
  SORT_ORDER_OPTIONS,
  SortField,
  SortOrder,
} from './utils';

interface SortButtonProps {
  selectedValue: SortField;
  selectedOrder: SortOrder;
  onSelect: (value: SortField) => void;
  onOrderChange: (value: SortOrder) => void;
  label?: string;
}

const SortButton: React.FC<SortButtonProps> = ({
  selectedValue,
  selectedOrder,
  onSelect,
  onOrderChange,
  label = 'Ordenar por',
}) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const validSelectedValue = isValidSortField(selectedValue)
    ? selectedValue
    : getDefaultSortField();

  const validSelectedOrder = isValidSortOrder(selectedOrder)
    ? selectedOrder
    : getDefaultSortOrder();

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }
    setOpen(false);
  };

  const handleFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (isValidSortField(value)) {
      onSelect(value);
    }
  };

  const handleOrderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (isValidSortOrder(value)) {
      onOrderChange(value);
    }
  };

  return (
    <React.Fragment>
      <Box
        ref={anchorRef}
        onClick={handleToggle}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'pointer',
          color: 'text.secondary',
          '&:hover': {
            color: 'text.primary',
          },
          transition: 'color 0.2s ease',
        }}
      >
        <FilterListIcon sx={{ fontSize: '1rem' }} />
        <Typography
          variant="body2"
          sx={{
            fontSize: '0.875rem',
            fontWeight: 400,
            userSelect: 'none',
          }}
        >
          {label}
        </Typography>
      </Box>
      <Popper
        sx={{ zIndex: 1300 }}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        placement="bottom-start"
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom-start'
                  ? 'top left'
                  : placement === 'bottom-end'
                    ? 'top right'
                    : 'bottom center',
            }}
          >
            <Paper
              sx={{
                mt: 1,
                minWidth: 200,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <Box sx={{ p: 0.5, py: 1 }}>
                  <RadioGroup
                    value={validSelectedValue}
                    onChange={handleFieldChange}
                  >
                    {SORT_OPTIONS.map((option) => (
                      <FormControlLabel
                        key={option.value}
                        value={option.value}
                        control={
                          <Radio
                            size="small"
                            sx={{
                              color: 'text.secondary',
                              '&.Mui-checked': {
                                color: 'primary.main',
                              },
                            }}
                          />
                        }
                        label={
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '0.875rem',
                              color: 'text.primary',
                            }}
                          >
                            {option.label}
                          </Typography>
                        }
                        sx={{
                          m: 0,
                          py: 0.25,
                          '& .MuiFormControlLabel-label': {
                            ml: 1,
                          },
                        }}
                      />
                    ))}
                  </RadioGroup>
                  <Box
                    sx={{
                      borderTop: '1px solid',
                      borderColor: 'divider',
                      my: 1,
                    }}
                  />
                  <RadioGroup
                    value={validSelectedOrder}
                    onChange={handleOrderChange}
                  >
                    {SORT_ORDER_OPTIONS.map((option) => (
                      <FormControlLabel
                        key={option.value}
                        value={option.value}
                        control={
                          <Radio
                            size="small"
                            sx={{
                              color: 'text.secondary',
                              '&.Mui-checked': {
                                color: 'primary.main',
                              },
                            }}
                          />
                        }
                        label={
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '0.875rem',
                              color: 'text.primary',
                            }}
                          >
                            {option.label}
                          </Typography>
                        }
                        sx={{
                          m: 0,
                          py: 0.25,
                          '& .MuiFormControlLabel-label': {
                            ml: 1,
                          },
                        }}
                      />
                    ))}
                  </RadioGroup>
                </Box>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </React.Fragment>
  );
};

export default SortButton;
export type { SortButtonProps };
export type { SortField, SortOption, SortOrder } from './utils';
export {
  getDefaultSortField,
  getDefaultSortOrder,
  isValidSortField,
  isValidSortOrder,
  SORT_OPTIONS,
  SORT_ORDER_OPTIONS,
  sortResells,
} from './utils';
